import {
    Badge,
    Button,
    Dialog,
    DialogContent,
    Tooltip,
} from "@jhool-io/fe-components";
import { useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import PencilLineIcon from "../../../../components/Icons/PencilLine";
import ShareIcon from "../../../../components/Icons/Share";
import RefreshIcon from "../../../../components/Icons/Refresh";
import MultiplyCircleIcon from "../../../../components/Icons/MultiplyCircle";
import ProfileDeleteIcon from "../../../../components/Icons/ProfileDelete";
import { TaskCategory, TaskStatus } from "../../types/tasks.types";
import useToast from "../../../../hooks/useToast";
import { useDeleteTasks, useUpdateTask } from "../../hooks/tasks.mutations";
import DeletePaymentIcon from "../../../../components/Icons/DeletePayment";
import { ViewEditorText } from "../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import ReassignTask from "../ReassignTask/ReassignTask";
import { useAppSelector } from "../../../../hooks/useRedux";
import CancelSessionForm from "../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import { ITask } from "../../../../utils/types/tasks";
import TaskDetails from "../TaskDetails/TaskDetails";
import useNewBackground from "../../../../hooks/useNewBackground";
import { formatDate } from "../../../../utils/helpers";

interface TaskProps {
    task: ITask;
}

type Dialogs =
    | "mark_as_completed"
    | "task_details"
    | "delete_task"
    | "forward_task"
    | "reassign_task"
    | "cancel_session"
    | "terminate_client";

export default function Task({ task }: TaskProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [taskDescription, setTaskDescription] = useState(
        () => task.description || ""
    );

    const [searchParams, setSearchParams] = useSearchParams();

    const useNewBg = useNewBackground();

    const params = useParams();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const loggedInUser = useFetchUserDetails();

    const updateTask = useUpdateTask();
    const deleteTask = useDeleteTasks();

    const { practice } = useAppSelector((state) => state.userPractice);

    const updateTaskIsMutating = useIsMutating(["update-todo"]);
    const isCancelSessionMutating = useIsMutating(["cancel-session"]);

    const handleDialogInView = (dialog: Dialogs | null) => {
        setDialogInView(dialog);
    };

    const getTaskLinkRoute = () => {
        if (
            task.status === TaskStatus.IN_PROGRESS &&
            task.category === TaskCategory.WRITE_SESSION_NOTE
        ) {
            return `/notes?status[]=draft`;
        }

        if (task.category === TaskCategory.WRITE_SESSION_NOTE) {
            return `/add-note?session_history_id=${task?.metadata?.session_history_id}`;
        }

        if (task.category === TaskCategory.MAKE_REQUESTED_CHANGE) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}?note_tab=change_requested`;
        }

        if (task.category === TaskCategory.REVIEW_SUPERVISEE_ADDENDUM) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}`;
        }

        if (task.category === TaskCategory.REVIEW_SUPERVISEE_NOTE) {
            return `/notes/${task?.metadata?.client_id}/${task?.metadata?.note_id}`;
        }

        if (
            task.category === TaskCategory.USER_CREATED &&
            task?.metadata?.client_id &&
            !params.clientId
        ) {
            return `/clients/${task?.metadata?.client_id}`;
        }

        return "";
    };

    const taskLinkUrl = getTaskLinkRoute();

    const handleOpenTaskDetailsDialog = () => {
        searchParams.set("task_id", task.todo_id);
        setSearchParams(searchParams);
        handleDialogInView("task_details");
    };

    const handleCloseDetailsDialog = () => {
        searchParams.delete("task_id");
        setSearchParams(searchParams, { replace: true });
        handleDialogInView(null);
    };

    const handleUpdateTask = (payload: {
        todoId: string;
        status?: TaskStatus;
        description?: string;
    }) => {
        const dataToSend = {
            todo_id: payload.todoId,
            status: payload.status || undefined,
            description: payload.description || undefined,
        };

        if (isEditing && !taskDescription) {
            toast({
                mode: "error",
                message: "Task description field cannot be empty",
            });
            return;
        }

        updateTask.mutate(
            { todos_to_update: [dataToSend] },
            {
                onSuccess: () => {
                    toast({
                        mode: "success",
                        message: "Task updated successfully",
                    });
                    setDialogInView(null);
                    queryClient.invalidateQueries({
                        queryKey: ["todos"],
                    });
                    if (isEditing) {
                        setIsEditing(false);
                        setTaskDescription("");
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update task at this time",
                    });
                },
            }
        );
    };

    const handleDeleteTask = () => {
        deleteTask.mutate([task.todo_id], {
            onSuccess: () => {
                toast({
                    mode: "success",
                    message: "Task deleted successfully",
                });
                handleDialogInView(null);
                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete task at this time",
                });
            },
        });
    };

    return (
        <>
            <Dialog open={dialogInView === "task_details"}>
                <DialogContent
                    title="Task Details"
                    handleCloseDialog={handleCloseDetailsDialog}
                    submitBtnFormValue="edit-task"
                    isSubmitBtnDisabled={updateTaskIsMutating > 0}
                    isCancelBtnDisabled={updateTaskIsMutating > 0}
                    showFooter={false}
                >
                    <TaskDetails />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "mark_as_completed"}>
                <DialogContent
                    title="Mark task as complete"
                    variant="center"
                    onSaveClick={() =>
                        handleUpdateTask({
                            todoId: task.todo_id,
                            status: TaskStatus.COMPLETED,
                        })
                    }
                    handleCloseDialog={() => setDialogInView(null)}
                    isCancelBtnDisabled={updateTask.isLoading}
                    isSubmitBtnDisabled={updateTask.isLoading}
                    classNames="pt-24 pb-48"
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            Are you sure you want to mark task as complete
                        </h3>
                        <p className="text-gray font-normal text-base">
                            By completing this task, you won’t be able to
                            recover task as you have marked and completed the
                            task.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "delete_task"}>
                <DialogContent
                    title="Delete task"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    isSubmitBtnDisabled={deleteTask.isLoading}
                    onSaveClick={handleDeleteTask}
                    isDeleting
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            Are you sure you want to delete this task
                        </h3>
                        <p className="text-gray font-normal text-base">
                            By deleting this task, you won’t be able to recover
                            task and this action can’t be undone
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog
                open={
                    dialogInView === "forward_task" ||
                    dialogInView === "reassign_task"
                }
            >
                <DialogContent
                    title={
                        dialogInView === "forward_task"
                            ? "Forward task"
                            : "Re-assign task"
                    }
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    submitBtnFormValue="reassign-task"
                    isCancelBtnDisabled={updateTaskIsMutating > 0}
                    isSubmitBtnDisabled={updateTaskIsMutating > 0}
                >
                    <ReassignTask
                        onFormSubmit={() => handleDialogInView(null)}
                        action={
                            dialogInView === "forward_task"
                                ? "forward"
                                : "reassign"
                        }
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    classNames="pt-24 pb-48"
                    canCancelButtonSubmit
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                        }}
                        sessionHistoryId={
                            task?.metadata?.session_history_id as string
                        }
                        shouldNavigate={shouldNavigate}
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "terminate_client"}>
                <DialogContent
                    title="Terminate Client"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => handleDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    classNames="pt-24 pb-48"
                    onCancelClick={() => handleDialogInView(null)}
                    cancelText="Cancel"
                    saveText="Terminate & Write Note"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                        }}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={
                            task?.metadata?.session_history_id as string
                        }
                        externalSessionId={task?.external_session_id || ""}
                        intakeClientId={task?.client?.intake_client_id || null}
                        action="terminate"
                    />
                </DialogContent>
            </Dialog>
            <div
                className={`p-12 rounded-r12 ${
                    useNewBg ? "bg-[#FFEBED]" : "bg-[rgba(255,235,237,0.32)]"
                } flex gap-x-[10px] items-start`}
            >
                <div>
                    <Tooltip content="Mark as complete">
                        <Button
                            variant="normal"
                            size="auto"
                            className="size-24"
                            disabled={updateTask.isLoading}
                            onClick={() => setDialogInView("mark_as_completed")}
                        >
                            <span className="size-20 rounded-full border-[2px] border-[#D3D5D9] bg-transparent" />
                        </Button>
                    </Tooltip>
                </div>
                <div className="flex flex-col items-start w-full">
                    <div className="mb-4 text-black">
                        {task.category === TaskCategory.USER_CREATED ? (
                            <Button
                                variant="normal"
                                size="auto"
                                className="!text-sm !font-medium leading-[20px]
                                tracking-[-0.084px] underline *:!m-0"
                                onClick={handleOpenTaskDetailsDialog}
                            >
                                {task.title ? (
                                    <ViewEditorText text={task.title} />
                                ) : (
                                    "--"
                                )}
                            </Button>
                        ) : (
                            <Link
                                to={taskLinkUrl}
                                className="!text-sm !font-medium leading-[20px]
                                tracking-[-0.084px] underline *:!m-0"
                            >
                                {task.title ? (
                                    <ViewEditorText text={task.title} />
                                ) : (
                                    "--"
                                )}
                            </Link>
                        )}
                    </div>
                    <div className="w-full mb-12">
                        {isEditing ? (
                            <>
                                <AddTextEditor
                                    defaultValue={taskDescription || ""}
                                    onEditorTextChange={setTaskDescription}
                                    isRequiredFieldProvided={Boolean(
                                        taskDescription
                                    )}
                                    reduceToolbaroptions
                                    editorClass="!h-[150px]"
                                />
                                <div className="flex justify-end gap-8 mt-12">
                                    <Button
                                        variant="secondary"
                                        aria-label="Cancel"
                                        onClick={() => setIsEditing(false)}
                                        disabled={updateTask.isLoading}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        onClick={() =>
                                            handleUpdateTask({
                                                todoId: task.todo_id,
                                                description: taskDescription,
                                            })
                                        }
                                        disabled={updateTask.isLoading}
                                    >
                                        Save changes
                                    </Button>
                                </div>
                            </>
                        ) : (
                            (task?.description && (
                                <div
                                    className={`${
                                        useNewBg
                                            ? "!text-[#717784]"
                                            : "!text-sub600"
                                    } !text-sm !font-normal leading-[20px] tracking-[-0.084px] *:!mb-0`}
                                >
                                    <ViewEditorText text={task.description} />
                                </div>
                            )) ||
                            "--"
                        )}
                    </div>
                    <div className="flex flex-col min-[500px]:flex-row gap-y-16 flex-wrap items-start min-[500px]:items-center justify-between gap-x-4 mt-12 w-full">
                        <Badge
                            content={`Due date: ${formatDate(task.due_date)}`}
                            bgColor="#FEC1C6"
                            textColor="#711920"
                        />
                        <div className="px-[6px] h-[28px] flex items-center gap-x-[18px] rounded-r6 bg-white border border-[rgba(231,231,231,0.91)] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)]">
                            <Tooltip content="Edit">
                                <Button
                                    className="w-24 h-20 hover:bg-[var(--color-gray-200)] !rounded-r4"
                                    variant="normal"
                                    size="auto"
                                    onClick={() => setIsEditing(true)}
                                    disabled={updateTask.isLoading}
                                >
                                    <PencilLineIcon />
                                </Button>
                            </Tooltip>
                            {loggedInUser.data?.user_id ===
                            task.creator.user_id ? (
                                <Tooltip content="Delete task">
                                    <Button
                                        className="w-24 h-20 hover:bg-[var(--color-gray-200)] !rounded-r4"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleDialogInView("delete_task")
                                        }
                                    >
                                        <DeletePaymentIcon
                                            stroke="#5A5F6A"
                                            strokeWidth="1"
                                        />
                                    </Button>
                                </Tooltip>
                            ) : null}
                            <Tooltip content="Forward task">
                                <Button
                                    className="w-24 h-20 hover:bg-[var(--color-gray-200)] !rounded-r4"
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        handleDialogInView("forward_task")
                                    }
                                >
                                    <ShareIcon />
                                </Button>
                            </Tooltip>
                            <Tooltip content="Reassign task">
                                <Button
                                    className="w-24 h-20 hover:bg-[var(--color-gray-200)] !rounded-r4"
                                    variant="normal"
                                    size="auto"
                                    onClick={() =>
                                        handleDialogInView("reassign_task")
                                    }
                                >
                                    <RefreshIcon />
                                </Button>
                            </Tooltip>
                            {practice?.provider_id &&
                            task?.metadata &&
                            task.metadata.session_history_id ? (
                                <Tooltip content="Report session as cancelled">
                                    <Button
                                        className="w-24 h-20 hover:bg-[var(--color-gray-200)] !rounded-r4"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleDialogInView("cancel_session")
                                        }
                                    >
                                        <MultiplyCircleIcon />
                                    </Button>
                                </Tooltip>
                            ) : null}
                            {practice?.provider_id &&
                            task?.metadata &&
                            task.metadata.session_history_id ? (
                                <Tooltip content="Terminate client">
                                    <Button
                                        className="w-24 h-20 bg-[#FB3748] !rounded-r4"
                                        variant="normal"
                                        size="auto"
                                        onClick={() =>
                                            handleDialogInView(
                                                "terminate_client"
                                            )
                                        }
                                    >
                                        <ProfileDeleteIcon />
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
